import React, { useState } from 'react';
import {
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    Box,
    Link,
    Alert,
    CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AuthService } from '../services/auth';

const ForgotPasswordPage: React.FC = () => {
    const [email, setEmail] = useState('');
    const [error, setError] = useState<{ message: string; reason: string } | null>(null);
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const getErrorMessage = (error: any): { message: string; reason: string } => {
        const errorCode = error?.code || '';
        
        switch (errorCode) {
            case 'auth/user-not-found':
                return {
                    message: 'Account not found',
                    reason: 'No account exists with this email address'
                };
            case 'auth/invalid-email':
                return {
                    message: 'Invalid email',
                    reason: 'Please enter a valid email address'
                };
            case 'auth/too-many-requests':
                return {
                    message: 'Too many attempts',
                    reason: 'Please try again later'
                };
            default:
                return {
                    message: 'Password reset failed',
                    reason: 'Please try again or contact support'
                };
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(false);
        setLoading(true);

        try {
            await AuthService.resetPassword(email);
            setSuccess(true);
            setEmail(''); // Clear the email field after success
        } catch (err) {
            console.error('Password reset error:', err);
            setError(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container maxWidth="sm" sx={{ mt: 8 }}>
            <Paper 
                elevation={3} 
                sx={{ 
                    p: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Typography 
                    variant="h4" 
                    component="h1" 
                    gutterBottom
                    sx={{ 
                        fontWeight: 700,
                        background: 'linear-gradient(145deg, #1e40af 0%, #2563eb 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                    }}
                >
                    Reset Password
                </Typography>

                <Typography variant="body1" sx={{ mb: 3, textAlign: 'center' }}>
                    Enter your email address and we'll send you a link to reset your password.
                </Typography>

                {error && (
                    <Alert 
                        severity="error" 
                        sx={{ 
                            width: '100%', 
                            mb: 2,
                            '& .MuiAlert-message': {
                                width: '100%'
                            }
                        }}
                    >
                        <Box sx={{ fontWeight: 'bold' }}>{error.message}</Box>
                        <Box sx={{ mt: 0.5, color: 'error.dark' }}>{error.reason}</Box>
                    </Alert>
                )}

                {success && (
                    <Alert 
                        severity="success" 
                        sx={{ width: '100%', mb: 2 }}
                    >
                        Password reset link has been sent to your email address.
                    </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="email"
                        label="Email Address"
                        name="email"
                        autoComplete="email"
                        autoFocus
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={loading}
                    />
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3, mb: 2 }}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : null}
                    >
                        {loading ? 'Sending...' : 'Send Reset Link'}
                    </Button>
                    <Box sx={{ textAlign: 'center' }}>
                        <Link 
                            href="/login" 
                            variant="body2"
                            sx={{ cursor: 'pointer' }}
                            onClick={(e) => {
                                e.preventDefault();
                                navigate('/login');
                            }}
                        >
                            Back to Login
                        </Link>
                    </Box>
                </Box>
            </Paper>
        </Container>
    );
};

export default ForgotPasswordPage; 